import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Paper,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';

import WalletBalanceIcon from '../components/icons/WalletBalanceIcon';
import HelpIcon from '../components/icons/HelpIcon';
import ChevronRightIcon from '../components/icons/ChevronRightIcon';
import TermsIcon from '../components/icons/TermsIcon';
import PrivacyPolicyIcon from '../components/icons/PrivacyPolicyIcon';
import RankBadge from '../components/RankBadge';
import apiService from '../services/api';
import liffService from '../services/liff';
import { useAuth } from '../auth/AuthContext';
import { UserMyPageMenu, SystemSetting, User, StoreUser, TierObject } from '../types';
import GamificationSection from '../components/GamificationSection';

// ティアの高さ比較用の数値（skill_rank_tiers.sort_order。0=ルーキー … 4=プラチナ、大きいほど上位）。
const tierSortValue = (t: TierObject | null | undefined): number =>
  t && typeof t.sort_order === 'number' ? t.sort_order : -1;

const UserMyPage: React.FC = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const [, setLoading] = useState<boolean>(true);
  const [, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [userInfo, setUserInfo] = useState<User | null>(null);
  const [storeUsers, setStoreUsers] = useState<StoreUser[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [tier, setTier] = useState<TierObject | null>(null);

  // 編集モード
  const [isEditMode, setIsEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  // IME変換確定タイミングとReact stateの反映がズレるケースに備え、
  // 保存時はstateではなくDOM上の実際の入力値を正とする
  const nameInputRef = useRef<HTMLInputElement>(null);

  // アバター
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('ファイルサイズが 5MB を超えています。');
      e.target.value = '';
      return;
    }
    setAvatarError(null);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  // settings からURLを引いて「その他」メニューを組み立てる
  const buildMenuItems = useCallback((): UserMyPageMenu[] => {
    const findUrl = (keyPattern: string) =>
      settings.find((s: SystemSetting) => s.setting_key.toLowerCase().includes(keyPattern))?.setting_value ?? '';
    return [
      { id: 1, title: 'FAQ・ヘルプ',         icon: HelpIcon,          url: '/help' },
      { id: 2, title: '利用規約',             icon: TermsIcon,         url: findUrl('terms') },
      { id: 3, title: 'プライバシーポリシー', icon: PrivacyPolicyIcon, url: findUrl('privacy') },
    ].filter((item) => item.url !== '' || item.id === 1) as UserMyPageMenu[];
  }, [settings]);

  // 外部URLかどうかを判定
  const isExternalUrl = (url: string) => /^https?:\/\//.test(url);

  // 外部URLを開く
  const handleExternalLink = (url: string) => {
    liffService.openWindow(url, true);
  };

  // メニューボタンのスタイル（下線なし）
  const ListItemBtnProp = {
    display: 'flex',
    justifyContent: 'flex-end',
  };
  const ListItemIconProp = { justifyContent: 'flex-end', minWidth: 'auto' };
const TypographyHeadingProp = { fontWeight: 'bold', p: 0, mb: 0.5 };

  // 編集/保存/キャンセルボタンの共通スタイル（幅を揃える）
  const EditBtnSx = {
    width: '100px',
    borderRadius: '16px',
    bgcolor: '#cce3f9',
    color: '#0071e3',
    fontSize: '13px',
    px: 2,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { bgcolor: '#b3d4f5', boxShadow: 'none' },
  };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsRes, userRes, storeUserRes] = await Promise.all([
        apiService.getSystemSetting(),
        userId ? apiService.getUser(userId) : Promise.resolve(null),
        userId ? apiService.getStoreUser(userId) : Promise.resolve(null),
      ]);

      if (settingsRes?.success && settingsRes.data) {
        setSettings(settingsRes.data.sort((a, b) => a.display_order - b.display_order));
      }
      if (userRes?.success && userRes.data) {
        setUserInfo(userRes.data);
      }
      const fetchedStoreUsers =
        storeUserRes?.success && storeUserRes.data && storeUserRes.data.length > 0
          ? storeUserRes.data
          : [];
      if (fetchedStoreUsers.length > 0) {
        setStoreUsers(fetchedStoreUsers);
      }

      // 氏名横バッジ = 店舗ランク（管理画面クルー管理と同じ worker_ranks を参照）。
      // LIFF の店舗文脈に一致する store_user があれば、その店舗の店舗ランクを表示する
      // （その店舗の管理画面クルー管理と一致する）。
      const currentStoreId = liffService.getStoreId();
      const matchedStoreUser = fetchedStoreUsers.find((su) => su.store_id === currentStoreId);
      if (matchedStoreUser) {
        const rankRes = await apiService.getStoreUserRank(matchedStoreUser.id);
        if (rankRes?.success && rankRes.data) {
          setTier(rankRes.data.tier ?? null);
        }
      } else if (fetchedStoreUsers.length > 0) {
        // 課題79: LIFF の店舗文脈が無い/一致しない場合、従来は先頭店舗（DB 任意順）を拾い、
        // 別店舗の低いランクを表示して「管理画面シルバー / マイページ ルーキー」不一致を招いていた。
        // worker_ranks は store_user 単位 1:1 なので、複数店舗登録時に “どの店舗の管理画面より
        // 低く出ない” よう、全登録店舗の中で最上位（sort_order 最大）の店舗ランクを表示する。
        const rankResults = await Promise.all(
          fetchedStoreUsers.map((su) => apiService.getStoreUserRank(su.id)),
        );
        let best: TierObject | null = null;
        for (const r of rankResults) {
          const t = r?.success && r.data ? r.data.tier ?? null : null;
          if (t && tierSortValue(t) > tierSortValue(best)) {
            best = t;
          }
        }
        if (best) {
          setTier(best);
        }
      } else if (userId) {
        // 店舗登録が全く無い場合のみ横断ランク（全店舗評価）にフォールバックする。
        const globalRankRes = await apiService.getGlobalRank(userId);
        if (globalRankRes?.success && globalRankRes.data) {
          setTier(globalRankRes.data.tier ?? null);
        }
      }
      if (userId) {
        const balanceRes = await apiService.getWalletBalance(userId);
        if (balanceRes?.success && balanceRes.data) {
          setBalance(balanceRes.data.total_balance ?? null);
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleWalletDetail = () => {
    const primaryStoreUserId = storeUsers[0]?.id;
    if (primaryStoreUserId) {
      localStorage.setItem('store_user_id', primaryStoreUserId);
      navigate('/wallet', { state: { storeUserId: primaryStoreUserId } });
      return;
    }
    navigate('/wallet');
  };

  const handleEditStart = () => {
    const name =
      [userInfo?.last_name, userInfo?.first_name].filter(Boolean).join(' ') ||
      userInfo?.name ||
      '';
    setEditName(name);
    setSaveAttempted(false);
    setAvatarFile(null);
    setAvatarPreview(null);
    setIsEditMode(true);
  };

  const handleSave = async () => {
    setSaveAttempted(true);
    // IME確定直後のクリックでReact stateの反映が間に合わないことがあるため、
    // DOM上の実際の入力値を優先して使う
    const liveValue = nameInputRef.current?.value ?? editName ?? '';
    const trimmedName = liveValue.trim();
    if (!trimmedName) return;
    try {
      setSaving(true);
      setAvatarError(null);
      const parts = trimmedName.split(/\s+/);
      const newLast = parts[0] ?? '';
      const newFirst = parts.slice(1).join(' ');
      if (!newFirst) {
        setAvatarError('姓と名の間にスペースを入力してください');
        return;
      }

      // アバターを先にアップロード（失敗時は氏名も更新せず編集モードを維持）
      let newAvatarUrl: string | undefined;
      if (avatarFile && userId) {
        const avatarRes = await apiService.uploadAvatar(userId, avatarFile);
        if (!avatarRes.success) {
          setAvatarError(avatarRes.error ?? 'アバターのアップロードに失敗しました');
          return;
        }
        newAvatarUrl = avatarRes.data?.profile_image_url ?? avatarPreview ?? undefined;
      }

      const updateRes = await apiService.updateUser(userId, { last_name: newLast, first_name: newFirst });
      if (!updateRes.success) {
        setAvatarError(updateRes.error ?? '保存に失敗しました');
        return;
      }

      // サーバーが実際に保存した値で更新する（ローカルの計算値を信用しない）
      setUserInfo((prev) =>
        prev
          ? {
              ...prev,
              last_name: updateRes.data?.last_name ?? newLast,
              first_name: updateRes.data?.first_name ?? newFirst,
              name: updateRes.data?.name ?? trimmedName,
              ...(newAvatarUrl ? { profile_image_url: newAvatarUrl } : {}),
            }
          : prev,
      );
      setAvatarFile(null);
      setAvatarPreview(null);
      setAvatarError(null);
      setIsEditMode(false);
    } catch (err) {
      setAvatarError('保存に失敗しました。通信環境をご確認のうえ再度お試しください。');
    } finally {
      setSaving(false);
    }
  };

  // 表示用の氏名
  const displayName =
    [userInfo?.last_name, userInfo?.first_name].filter(Boolean).join(' ') ||
    userInfo?.name ||
    '---';

  return (
    <Box sx={{ p: 0 }}>
      {/* 戻るボタン行 */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1.5, pb: 0.5, minHeight: 48 }}>
        <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: '#3c3c3c' }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* プロフィール: アバター + 氏名 + ランクバッジ + 編集/保存/キャンセル */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 4,
          pt: 1,
          pb: 2,
        }}
      >
        {/* アバター */}
        <Box sx={{ position: 'relative', flexShrink: 0, mb: isEditMode ? 1.5 : 0 }}>
          <Box
            onClick={isEditMode ? () => fileInputRef.current?.click() : undefined}
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#c5c5c5',
              overflow: 'hidden',
              cursor: isEditMode ? 'pointer' : 'default',
            }}
          >
            {(avatarPreview ?? userInfo?.profile_image_url) && (
              <img
                src={avatarPreview ?? userInfo?.profile_image_url}
                alt="avatar"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Box>
          {isEditMode && (
            <Button
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                borderRadius: '16px',
                bgcolor: '#cce3f9',
                color: '#0071e3',
                fontSize: '10px',
                px: 1,
                py: 0.25,
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                minWidth: 0,
                lineHeight: 1.4,
                '&:hover': { bgcolor: '#b3d4f5', boxShadow: 'none' },
              }}
            >
              画像を挿入する
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
          />
          {avatarError && (
            <Typography
              sx={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                mt: 1.5,
                fontSize: '10px',
                color: '#d32f2f',
                whiteSpace: 'nowrap',
                textAlign: 'center',
              }}
            >
              {avatarError}
            </Typography>
          )}
        </Box>

        {/* 氏名 + ランクバッジ */}
        <Box sx={{ flex: 1 }}>
          {isEditMode ? (
            <TextField
              inputRef={nameInputRef}
              value={editName ?? ''}
              onChange={(e) => setEditName(e.target.value)}
              size="small"
              error={saveAttempted && !(editName ?? '').trim()}
              helperText={saveAttempted && !(editName ?? '').trim() ? '氏名を入力してください' : ''}
              fullWidth
              inputProps={{ style: { fontWeight: 700, fontSize: '16px' } }}
            />
          ) : (
            <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#1a1a1a', mb: 0.5 }}>
              {displayName}
            </Typography>
          )}
          <RankBadge tier={tier} size={22} labelFontSize="13px" />
        </Box>

        {/* 編集/保存/キャンセル */}
        {isEditMode ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0, alignSelf: 'flex-start' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving}
              sx={{
                ...EditBtnSx,
                '&.Mui-disabled': { bgcolor: '#cce3f9', color: '#7ab0e0' },
              }}
            >
              {saving ? <CircularProgress size={16} sx={{ color: '#0071e3' }} /> : '保存'}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => { setIsEditMode(false); setAvatarFile(null); setAvatarPreview(null); }}
              sx={{ ...EditBtnSx }}
            >
              キャンセル
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={handleEditStart}
            sx={{ ...EditBtnSx, flexShrink: 0, alignSelf: 'flex-start' }}
          >
            編集
          </Button>
        )}
      </Box>

      {/* 残高カード */}
      <Box sx={{ px: 4, mb: 3 }}>
        <Paper
          elevation={1}
          sx={{
            borderRadius: 3,
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: '#e0f7f4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WalletBalanceIcon color="#13c3a8" size={26} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', lineHeight: 1.2 }}>
                現在の残高
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '22px', color: '#1a1a1a', lineHeight: 1.4 }}>
                {balance !== null ? `¥${balance.toLocaleString()}` : '---'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleWalletDetail}
            sx={{
              borderRadius: 5,
              border: 'none',
              bgcolor: '#cceceb',
              color: '#00a29a',
              fontWeight: 700,
              fontSize: '12px',
              whiteSpace: 'nowrap',
              px: 1.5,
              '&:hover': { border: 'none', bgcolor: '#b8e3e1' },
            }}
          >
            {userInfo?.sokyu_opt_out ? '詳細' : '詳細・出金'}
          </Button>
        </Paper>
      </Box>

      {/* スキル / ランク（編集モード中は獲得スキルのみ非表示） */}
      <GamificationSection userId={userId} storeUsers={storeUsers} hideSkills={isEditMode} />
      <Divider sx={{ mx: 4, mb: 3 }} />

      {/* メニューセクション */}
      <Box sx={{ px: 4 }}>

        {/* プロフィール変更。
            画面上部の「編集」は姓名・アイコンのみ。生年月日・電話番号・メールアドレスは
            店舗の事前登録との名寄せキーであり、相違があると店舗側に「LINE未連携」の行が
            残るため、本人が確認・修正できる導線をここに置く（FAQ「店舗から登録済みと
            言われたのに求人が出てこない」の自己解決導線）。 */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...TypographyHeadingProp }}>プロフィール</Typography>
          <List sx={{ p: 0 }}>
            <ListItem sx={{ p: 0 }}>
              <ListItemButton sx={{ ...ListItemBtnProp }} component={RouterLink} to="/profile">
                <ListItemIcon sx={{ minWidth: 40 }}><PersonIcon sx={{ color: '#898989' }} /></ListItemIcon>
                <ListItemText>プロフィール変更（氏名・生年月日・連絡先）</ListItemText>
                <ListItemIcon sx={{ ...ListItemIconProp }}><ChevronRightIcon /></ListItemIcon>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* シフト予定表 */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...TypographyHeadingProp }}>シフト予定表</Typography>
          <List sx={{ p: 0 }}>
            <ListItem sx={{ p: 0 }}>
              <ListItemButton sx={{ ...ListItemBtnProp }} component={RouterLink} to="/my-applications">
                <ListItemIcon sx={{ minWidth: 40 }}><HistoryIcon sx={{ color: '#898989' }} /></ListItemIcon>
                <ListItemText>今日以降のシフトを確認する</ListItemText>
                <ListItemIcon sx={{ ...ListItemIconProp }}><ChevronRightIcon /></ListItemIcon>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* 労働条件通知書 */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...TypographyHeadingProp }}>労働条件通知書</Typography>
          <List sx={{ p: 0 }}>
            <ListItem sx={{ p: 0 }}>
              <ListItemButton sx={{ ...ListItemBtnProp }} component={RouterLink} to="/labor-conditions">
                <ListItemIcon sx={{ minWidth: 40 }}><DescriptionIcon sx={{ color: '#898989' }} /></ListItemIcon>
                <ListItemText>同意した通知書をダウンロードする</ListItemText>
                <ListItemIcon sx={{ ...ListItemIconProp }}><ChevronRightIcon /></ListItemIcon>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* 登録している店舗 */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...TypographyHeadingProp }}>登録している店舗</Typography>
          <List sx={{ p: 0 }}>
            {storeUsers.length === 0 ? (
              <ListItem sx={{ p: 0 }}>
                <ListItemButton sx={{ ...ListItemBtnProp }} disabled>
                  <ListItemText sx={{ color: '#aaa' }}>登録されている店舗がありません</ListItemText>
                </ListItemButton>
              </ListItem>
            ) : (
              storeUsers.map((su) => (
                <ListItem key={su.id} sx={{ p: 0 }}>
                  <ListItemButton
                    sx={{ ...ListItemBtnProp }}
                    component={RouterLink}
                    to={`/store-Management/${su.id}`}
                  >
                    <ListItemText>{su.store_name}</ListItemText>
                    <ListItemIcon sx={{ ...ListItemIconProp }}><ChevronRightIcon /></ListItemIcon>
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* 振込先口座（即給非利用ワーカーには表示しない） */}
        {!userInfo?.sokyu_opt_out && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ ...TypographyHeadingProp }}>振込先口座</Typography>
              <List sx={{ p: 0 }}>
                <ListItem sx={{ p: 0 }}>
                  <ListItemButton sx={{ ...ListItemBtnProp }} component={RouterLink} to="/bank-account">
                    <ListItemIcon sx={{ minWidth: 40 }}><AccountBalanceIcon sx={{ color: '#898989' }} /></ListItemIcon>
                    <ListItemText>口座情報を登録・変更する</ListItemText>
                    <ListItemIcon sx={{ ...ListItemIconProp }}><ChevronRightIcon /></ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        {/* その他 */}
        <Box>
          <Typography sx={{ ...TypographyHeadingProp }}>その他</Typography>
          <List sx={{ p: 0 }}>
            {buildMenuItems().map((menus) => (
              <ListItem key={menus.id} sx={{ p: 0 }}>
                {isExternalUrl(menus.url) ? (
                  <ListItemButton sx={{ ...ListItemBtnProp }} onClick={() => handleExternalLink(menus.url)}>
                    <ListItemIcon sx={{ minWidth: 40 }}><menus.icon color="#898989" /></ListItemIcon>
                    <ListItemText>{menus.title}</ListItemText>
                    <ListItemIcon sx={{ ...ListItemIconProp }}><ChevronRightIcon /></ListItemIcon>
                  </ListItemButton>
                ) : (
                  <ListItemButton sx={{ ...ListItemBtnProp }} component={RouterLink} to={menus.url}>
                    <ListItemIcon sx={{ minWidth: 40 }}><menus.icon color="#898989" /></ListItemIcon>
                    <ListItemText>{menus.title}</ListItemText>
                    <ListItemIcon sx={{ ...ListItemIconProp }}><ChevronRightIcon /></ListItemIcon>
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
        </Box>

      </Box>
    </Box>
  );
};

export default UserMyPage;
